import IMask from 'imask';

/**
 * Třída pro validaci a formátování vstupních polí pomocí IMask
 */
class InputsValidator {
    constructor() {
        // Mapa pro ukládání instancí masek
        this.masks = new Map();
    }

    /**
     * Inicializace validátorů pro všechna vstupní pole
     */
    init() {
        this.initializeInputValidators();
    }

    /**
     * Nastavení posluchačů událostí pro NAJA a DOM
     * @param {Object} naja - Instance NAJA objektu
     */
    initialize(naja) {
        if (naja) {
            naja.addEventListener('complete', this.init.bind(this));
            naja.addEventListener('interaction', this.init.bind(this));
        }
        this.init();
    }

    /**
     * Vytvoří masku pro číselné vstupy
     * @param {HTMLInputElement} input - Vstupní element
     * @param {number} digits - Maximální počet číslic
     * @param {number} decimals - Počet desetinných míst
     * @param {boolean} allowNegative - Povolit záporné hodnoty
     * @returns {IMask.InputMask} - Instance IMask
     */
    createNumberMask(input, digits, decimals, allowNegative = false) {
        if (this.masks.has(input)) {
            // Zničit existující masku, pokud existuje
            this.masks.get(input).destroy();
        }

        // Vytvořit novou masku
        const maskOptions = {
            mask: Number,
            scale: decimals,  // počet desetinných míst
            signed: allowNegative, // povolení záporných hodnot
            thousandsSeparator: '', // bez oddělovače tisíců
            padFractionalZeros: false, // nezarovnávat nulami
            normalizeZeros: true,
            radix: '.', // oddělovač desetinných míst
            mapToRadix: [','], // mapování čárky na oddělovač desetinných míst
            max: Math.pow(10, digits) - Math.pow(0.1, decimals) // maximální povolená hodnota
        };

        const mask = IMask(input, maskOptions);
        this.masks.set(input, mask);

        return mask;
    }

    /**
     * Vytvoří masku pro PSČ
     * @param {HTMLInputElement} input - Vstupní element
     * @returns {IMask.InputMask} - Instance IMask
     */
    createPostalCodeMask(input) {
        if (this.masks.has(input)) {
            this.masks.get(input).destroy();
        }

        const mask = IMask(input, {
            mask: '000 00',
            lazy: false
        });

        this.masks.set(input, mask);
        return mask;
    }

    /**
     * Inicializace validátorů pro všechny typy vstupních polí
     */
    initializeInputValidators() {
        // Vyhledat všechny potřebné inputy podle typu
        const priceInputs = document.querySelectorAll(
            'input[name="pricePurchase"], ' +
            'input[name="pricePurchase"], ' +
            'input[name="purchasePrice"], ' +
            'input[name="feeRecycle"], ' +
            'input[name="feeCopyright"], ' +
            'input[name="salePriceWithoutFees"], ' +
            'input[name="salePriceTotal"], ' +
            'input[name="salePrice"], ' +
            'input[name="averagePrice"], ' +
            'input[name="actionPrice"], ' +
            'input[name="discount"], ' +

            'input[name="inline_add[count]"], ' +
            'input[name="inline_add[price]"], ' +
            'input[name="inline_add[priceBase]"], ' +
            'input[name="inline_add[priceTotal]"], ' +
            'input[name="inline_add[pricePurchase]"], ' +
            'input[name="inline_add[purchasePrice]"], ' +
            'input[name="inline_add[feeRecycle]"], ' +
            'input[name="inline_add[feeCopyright]"], ' +
            'input[name="inline_add[salePriceWithoutFees]"], ' +
            'input[name="inline_add[salePriceTotal]"], ' +
            'input[name="inline_add[salePrice]"], ' +
            'input[name="inline_add[priceSale]"], ' +
            'input[name="inline_add[averagePrice]"], ' +
            'input[name="inline_add[actionPrice]"], '+
            'input[name="inline_edit[discount]"], ' +

            'input[name="inline_edit[count]"], ' +
            'input[name="inline_edit[price]"], ' +
            'input[name="inline_edit[priceBase]"], ' +
            'input[name="inline_edit[priceTotal]"], ' +
            'input[name="inline_edit[pricePurchase]"], ' +
            'input[name="inline_edit[purchasePrice]"], ' +
            'input[name="inline_edit[feeRecycle]"], ' +
            'input[name="inline_edit[feeCopyright]"], ' +
            'input[name="inline_edit[salePriceWithoutFees]"], ' +
            'input[name="inline_edit[salePriceTotal]"], ' +
            'input[name="inline_edit[salePrice]"], ' +
            'input[name="inline_edit[priceSale]"], ' +
            'input[name="inline_edit[averagePrice]"], ' +
            'input[name="inline_edit[actionPrice]"], ' +
            'input[name="inline_edit[discount]"]'
        );

        const marginInputs = document.querySelectorAll('input[name="saleMargin"]');
        const taxInputs = document.querySelectorAll('input[name="purchaseTax"], input[name="frmcard-saleTax"]');
        const discountInputs = document.querySelectorAll('input[name="discount"], input[name="inline_add[discount]"], input[name="inline_edit[discount]"]');
        const postalInputs = document.querySelectorAll('input[name="postal"]');

        // Aplikovat masky podle typu
        priceInputs.forEach(input => this.createNumberMask(input, 10, 4));
        marginInputs.forEach(input => this.createNumberMask(input, 4, 2));
        taxInputs.forEach(input => this.createNumberMask(input, 2, 2));
        discountInputs.forEach(input => this.createNumberMask(input, 3, 2));
        postalInputs.forEach(input => this.createPostalCodeMask(input));
    }

    /**
     * Aktualizace hodnoty v inputu s použitím masky
     * @param {HTMLInputElement} input - Vstupní element
     * @param {number|string} value - Hodnota, která má být nastavena
     * @returns {string} - Formátovaná hodnota
     */
    updateMaskedValue(input, value) {
        if (this.masks.has(input)) {
            const mask = this.masks.get(input);
            mask.value = value.toString();
            return mask.value;
        } else {
            // Pokud maska neexistuje, vytvořit ji podle typu
            let digits = 10;
            let decimals = 4;

            if (input.name === 'saleMargin') {
                digits = 4;
                decimals = 2;
            } else if (input.name === 'purchaseTax' || input.name === 'frmcard-saleTax') {
                digits = 2;
                decimals = 2;
            }

            const mask = this.createNumberMask(input, digits, decimals);
            mask.value = value.toString();
            return mask.value;
        }
    }

    /**
     * Získání instance masky pro daný vstupní element
     * @param {HTMLInputElement} input - Vstupní element
     * @returns {IMask.InputMask|null} - Instance masky nebo null
     */
    getMask(input) {
        return this.masks.get(input) || null;
    }

    /**
     * Formátování čísla na daný počet desetinných míst
     * @param {number} value - Hodnota k formátování
     * @param {number} decimals - Počet desetinných míst
     * @returns {string} - Formátovaná hodnota
     */
    formatNumber(value, decimals) {
        return parseFloat(value).toFixed(decimals).replace('.', ',');
    }

    /**
     * Odebrání všech masek a čištění zdrojů
     */
    destroy() {
        this.masks.forEach(mask => {
            mask.destroy();
        });
        this.masks.clear();
    }
}


// Exportovat třídu
export default InputsValidator;
